import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicExhibitionItem,
  PublicSessionItem,
  Portfolio,
  ResponsePaginationType,
  PublicAttendee,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Image, Link, Text, View } from '@react-pdf/renderer';
import { useCallback } from 'react';
import { APP_HOST } from '@utils/apiHooks';

import worldIcon from '@assets/icons/world_icon.png';
import worldIconBlack from '@assets/icons/world_icon_black.png';
import mailIcon from '@assets/icons/mail_icon.png';
import mailIconBlack from '@assets/icons/mail_icon_black.png';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'styled-components';
import { styles } from './styles';

import TitlePage from './pages/TitlePage';
import ClaimPage from './pages/ClaimPage';
import { MULTI_TOUCHPOINT_ITEMS, SPONSOR_EMAIL } from '@utils/constants';
import WhoAndWhyPage from './pages/WhoAndWhyPage';
import MatchMakingPage from './pages/MatchMakingPage';
import WhyThisEventMattersPage from './pages/WhyThisEventMattersPage';
import MultitouchpointPage from './pages/MultitouchpointPage';
import KeyTopicsPage from './pages/KeyTopicsPage';
import NETPromoterScorePage from './pages/NETPromoterScorePage';
import ListOfAttendeesPage from './pages/ListOfAttendeesPage';
import CoLocatedEventsPage from './pages/CoLocatedEventsPage';
import AllSpeakerPage from './pages/AllSpeakerPage';
import PartnerWithUsPage from './pages/PartnerWithUsPage';
import PartnerThisYearPage from './pages/PartnerThisYearPage';
import { Ticket } from '@composites/Tickets';
import PackagePage from './pages/PackagePage';
import GetInTouchPage from './pages/GetInTouchPage';
import CommunityFeedbackPage from './pages/CommunityFeedbackPage';
import EventSeriesPage from './pages/EventSeriesPage';
import { getImageSrc } from '@utils/fn';

interface BusinessPartnerPdfContentProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  theme: DefaultTheme;
  groupedAgendasSessions: PublicSessionItem[][];
  agendaPdfDates: string[];
  agendaSpeakers: AgendaSpeaker[];
  exhibitions: PublicExhibitionItem[];
  advertisements: any[] | undefined;
  attendees: PublicAttendee[] | undefined;
  tickets: Ticket[];
  eventSeries: PublicEventDetailsResponse[];
}

const BusinessPartnerPdfContent = ({
  event,
  coLocatedEvents,
  portfolios,
  groupedAgendasSessions,
  agendaPdfDates,
  agendaSpeakers,
  exhibitions,
  advertisements,
  attendees,
  tickets,
  eventSeries,
}: BusinessPartnerPdfContentProps) => {
  const { t } = useTranslation();

  const currentPortfolio = portfolios?.data?.find(
    p => p.id === event?.portfolio?.id
  );
  const portfolioUrl = currentPortfolio?.slug
    ? `${APP_HOST}${t('mappable-company-routes.our-product-portfolio-url')}/${currentPortfolio.slug}`
    : '';

  const portfolioDesign = (currentPortfolio?.design ?? {}) as any;

  const seriesLogoNeg = portfolioDesign?.logoNegImagePng
    ? getImageSrc(portfolioDesign.logoNegImagePng)
    : undefined;

  const seriesLogoPos = event?.design?.eventLogoPosSvg
    ? getImageSrc(event.design.eventLogoPosSvg)
    : undefined;

  const seriesLogoFallback =
    'https://media.we-conect.com/images/website/smart_mobility_neg.png';
  const Footer = useCallback(
    ({
      leftLightMode,
      rightLightMode,
      noPadding = false,
      fontSize = 14,
    }: {
      leftLightMode?: boolean;
      rightLightMode?: boolean;
      noPadding?: boolean;
      fontSize?: number;
    }) => {
      return (
        <View
          style={{
            ...styles.footer,
            backgroundColor:
              leftLightMode === rightLightMode && leftLightMode === false
                ? event?.design?.themeColor
                : 'transparent',
            width:
              leftLightMode === rightLightMode && leftLightMode === false
                ? '98%'
                : '100%',
            marginLeft:
              leftLightMode === rightLightMode && leftLightMode === false
                ? 15
                : 0,
          }}
          fixed
        >
          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: leftLightMode
                ? 'white'
                : event?.design?.themeColor,
              borderTopLeftRadius: leftLightMode === rightLightMode ? '8px' : 0,
              color: leftLightMode ? '#27343d' : 'white',
              alignItems: 'flex-start',
              paddingLeft: noPadding
                ? 0
                : leftLightMode === rightLightMode && leftLightMode === false
                  ? 38
                  : 53,
            }}
          >
            <Link src={portfolioUrl} style={{ textDecoration: 'none' }}>
              <Image
                style={styles.logo}
                src={
                  leftLightMode
                    ? seriesLogoPos || seriesLogoNeg || seriesLogoFallback
                    : seriesLogoNeg || seriesLogoPos || seriesLogoFallback
                }
              />
            </Link>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: leftLightMode
                ? 'white'
                : event?.design?.themeColor,
              color: leftLightMode ? '#27343d' : 'white',
            }}
          >
            <View
              style={{
                ...styles.footerMiddleItem,
                color: leftLightMode ? '#27343d' : 'white',
                fontSize: fontSize,
              }}
            >
              <Image
                src={leftLightMode ? worldIconBlack : worldIcon}
                style={styles.icon}
              />
              {portfolioUrl ? (
                <Link
                  src={portfolioUrl}
                  style={{
                    color: leftLightMode ? '#27343d' : 'white',
                    textDecoration: 'none',
                  }}
                >
                  {t('view-event-series-here')}
                </Link>
              ) : (
                <Text>{t('view-event-series-here')}</Text>
              )}
            </View>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: rightLightMode
                ? 'white'
                : event?.design?.themeColor,
              color: rightLightMode ? '#27343d' : 'white',
            }}
          >
            <View
              style={{
                ...styles.footerMiddleItem,
                backgroundColor: rightLightMode
                  ? 'white'
                  : event?.design?.themeColor,
                color: rightLightMode ? '#27343d' : 'white',
                fontSize: fontSize,
              }}
            >
              <Image
                src={rightLightMode ? mailIconBlack : mailIcon}
                style={styles.icon}
              />
              <Link
                href={`mailto:${SPONSOR_EMAIL}`}
                style={{
                  color: rightLightMode ? '#27343d' : 'white',
                  textDecoration: 'none',
                }}
              >
                {SPONSOR_EMAIL}
              </Link>
            </View>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: rightLightMode
                ? 'white'
                : event?.design?.themeColor,
              borderTopRightRadius:
                leftLightMode === rightLightMode ? '8px' : 0,
              color: rightLightMode ? '#27343d' : 'white',
              alignItems: 'flex-end',
              paddingRight:
                leftLightMode === rightLightMode && leftLightMode === false
                  ? 38
                  : 53,
            }}
          >
            <Link
              src="https://www.we-conect.com/"
              style={{ textDecoration: 'none' }}
            >
              <Image
                style={styles.logo}
                src={
                  rightLightMode
                    ? 'https://media.we-conect.com/images/pdf/partnerprospectus/weCONECT_mono_black.png'
                    : 'https://media.we-conect.com/images/pdf/partnerprospectus/weCONECT_mono_white.png'
                }
              />
            </Link>
          </View>
        </View>
      );
    },
    [event?.design?.themeColor, portfolioUrl, t]
  );

  const FooterTwoRows = useCallback(
    ({
      leftLightMode,
      rightLightMode,
    }: {
      leftLightMode?: boolean;
      rightLightMode?: boolean;
    }) => (
      <View
        style={{
          ...styles.footer,
          flexDirection: 'row',
          alignItems: 'stretch',
          backgroundColor:
            leftLightMode === rightLightMode && leftLightMode === false
              ? event?.design?.themeColor
              : 'transparent',
          width:
            leftLightMode === rightLightMode && leftLightMode === false
              ? '98%'
              : '100%',
          marginLeft:
            leftLightMode === rightLightMode && leftLightMode === false
              ? 15
              : 0,
          height: 120,
          paddingLeft: 50,
        }}
        fixed
      >
        {/* LEFT: two icons stacked vertically */}
        <View
          style={{
            ...styles.footerColumn,
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            backgroundColor: leftLightMode
              ? 'white'
              : event?.design?.themeColor,
            borderTopLeftRadius: leftLightMode === rightLightMode ? '8px' : 0,
            color: leftLightMode ? '#27343d' : 'white',
            height: 120,
          }}
        >
          <View
            style={{
              ...styles.footerMiddleItem,
              color: leftLightMode ? '#27343d' : 'white',
              justifyContent: 'flex-start',
            }}
          >
            <Image
              src={leftLightMode ? worldIconBlack : worldIcon}
              style={styles.icon}
            />
            {portfolioUrl ? (
              <Link
                src={portfolioUrl}
                style={{
                  color: leftLightMode ? '#27343d' : 'white',
                  textDecoration: 'none',
                }}
              >
                {t('view-event-series-here')}
              </Link>
            ) : (
              <Text>{t('view-event-series-here')}</Text>
            )}
          </View>
          <View
            style={{
              ...styles.footerMiddleItem,
              color: leftLightMode ? '#27343d' : 'white',
              justifyContent: 'flex-start',
            }}
          >
            <Image
              src={rightLightMode ? mailIconBlack : mailIcon}
              style={styles.icon}
            />
            <Link
              href={`mailto:${SPONSOR_EMAIL}`}
              style={{
                color: leftLightMode ? '#27343d' : 'white',
                textDecoration: 'none',
              }}
            >
              {SPONSOR_EMAIL}
            </Link>
          </View>
        </View>

        {/* RIGHT: two logos stacked vertically */}
        <View
          style={{
            ...styles.footerColumn,
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            backgroundColor: rightLightMode
              ? 'white'
              : event?.design?.themeColor,
            borderTopRightRadius: leftLightMode === rightLightMode ? '8px' : 0,
            color: rightLightMode ? '#27343d' : 'white',
            height: 120,
          }}
        >
          <Image
            style={styles.logo}
            src={
              rightLightMode
                ? 'https://media.we-conect.com/images/pdf/partnerprospectus/weCONECT_mono_black.png'
                : 'https://media.we-conect.com/images/pdf/partnerprospectus/weCONECT_mono_white.png'
            }
          />
          <Image
            style={styles.logo}
            src={
              rightLightMode
                ? 'https://media.we-conect.com/images/pdf/partnerprospectus/weCONECT_mono_black.png'
                : 'https://media.we-conect.com/images/pdf/partnerprospectus/weCONECT_mono_white.png'
            }
          />
        </View>
      </View>
    ),
    [event?.design?.themeColor, event?.slug, t, portfolioUrl]
  );

  const account: any = currentPortfolio?.account;
  const packages = (account?.packages || [])?.filter(
    (p: any) => p?.type === 'bp'
  );

  return (
    <Document>
      <TitlePage
        event={event}
        footer={
          <Footer leftLightMode={false} rightLightMode={false} fontSize={11} />
        }
      />

      <ClaimPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={true} />}
      />

      <WhoAndWhyPage
        event={event}
        footer={<Footer leftLightMode={true} rightLightMode={false} />}
      />

      <MatchMakingPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
      />

      {/*
<WhyThisEventMattersPage
  event={event}
  footer={
    <Footer
      leftLightMode={true}
      rightLightMode={true}
      noPadding
      fontSize={13}
    />
  }
/>
*/}

      <MultitouchpointPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
        paragraphs={[
          'multitouchpoint-paragraph-1',
          'multitouchpoint-paragraph-2',
        ]}
        multiTouchPointItems={MULTI_TOUCHPOINT_ITEMS(t, event?.slug).slice(
          0,
          5
        )}
      />

      <MultitouchpointPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
        paragraphs={[
          'multitouchpoint-paragraph-3',
          'multitouchpoint-paragraph-4',
        ]}
        multiTouchPointItems={MULTI_TOUCHPOINT_ITEMS(t, event?.slug).slice(
          5,
          10
        )}
        inverse={true}
      />

      <KeyTopicsPage
        event={event}
        footer={<Footer leftLightMode={true} rightLightMode={true} />}
      />

      <CoLocatedEventsPage
        event={event}
        coLocatedEvents={coLocatedEvents}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
      />

      <PartnerThisYearPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
        exhibitions={exhibitions}
      />

      <ListOfAttendeesPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
        attendees={attendees}
      />

      <AllSpeakerPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
        agendaSpeakers={agendaSpeakers}
      />
      <CommunityFeedbackPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
      />

      <NETPromoterScorePage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={false} />}
      />

      <PartnerWithUsPage
        event={event}
        footer={<Footer leftLightMode={false} rightLightMode={true} />}
      />

      {packages?.map((p: any) => {
        return (
          <PackagePage
            key={p?.slug}
            event={event}
            packageItem={p}
            footer={
              <FooterTwoRows leftLightMode={true} rightLightMode={true} />
            }
          />
        );
      })}

      <EventSeriesPage
        event={event}
        eventSeries={eventSeries}
        portfolio={currentPortfolio}
        footer={<Footer leftLightMode={true} rightLightMode={true} />}
      />
      {/*
      <GetInTouchPage
        event={event}
        portfolio={currentPortfolio}
        footer={<Footer leftLightMode={true} rightLightMode={false} />}
      />
      */}
    </Document>
  );
};

export default BusinessPartnerPdfContent;
